import { EmptyState } from '@/components/molecules/EmptyState'
import { useRobotAlerts } from '@/hooks/useRobotAlerts'
import { useRobotsRoster } from '@/hooks/useRobotsRoster'
import type { RobotMediaItem } from '@/services/api/domains/robots.api'
import type { RobotAlertItem } from '@/services/api/robot-alerts-store'
import { BellOff } from 'lucide-react'
import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useDashboardBridgeOptional } from '../Dashboard/useDashboardBridge'
import { RobotMediaLightbox } from '../RobotMedia/RobotMediaLightbox'
import { indexRoster, resolveAlertLabel, resolveDeviceKind } from './alert-display'
import { buildLogEntries, filterAlerts, mergeAlertLog, SERVER_LIST_CAP, type AlertLogScope } from './alert-log'
import { normalizeSeverity, type AlertSeverity } from './alert-severity'
import { AlertLogHeader } from './AlertLogHeader'
import { AlertLogRow } from './AlertLogRow'
import { ClearAllConfirm } from './ClearAllConfirm'
import { EnrollFaceModal } from './EnrollFaceModal'
import { PictureLogRow } from './PictureLogRow'
import { useAlertLogFeed } from './useAlertLogFeed'

/**
 * The alert LOG — every alarm occurrence and, for a selected helmet, every picture, newest first,
 * nothing dismissed. It replaced an inbox whose per-row Acknowledge/Resolve the operators found
 * useless: a row here records what happened, where, and when; it does not ask to be cleared.
 *
 * Two sources feed it (the live store for open alerts, a fetch for handled ones and pictures) —
 * see `useAlertLogFeed` for when the fetch runs and `alert-log.ts` for how rows are built.
 */
export interface AlertInboxProps {
  /** `{ kind: 'fleet' }` (the default) shows every alert the operator can see; `robot` narrows. */
  scope?: AlertLogScope
  /**
   * When a non-empty array, keep only alerts whose severity is in it. Saved configs carrying the
   * legacy `warning` are read as `high` (see `normalizeSeverity`).
   */
  severityFilter?: Array<AlertSeverity | 'warning'>
  /** This dashboard element's id, so its own refresh tick reaches the log. Absent in the drawer. */
  elementId?: string
}

const FLEET: AlertLogScope = { kind: 'fleet' }

function errorText(err: unknown, fallback: string): string {
  const message = err instanceof Error ? err.message.trim() : ''

  return message || fallback
}

export function AlertInbox({ scope = FLEET, severityFilter, elementId }: AlertInboxProps = {}) {
  const { t } = useTranslation('console')
  const { alerts: storeAlerts, clearAll } = useRobotAlerts()
  const bridge = useDashboardBridgeOptional()
  const feed = useAlertLogFeed(scope, storeAlerts, elementId)
  const { robots } = useRobotsRoster()
  const rosterById = useMemo(() => indexRoster(robots ?? []), [robots])

  const [enrollAlert, setEnrollAlert] = useState<RobotAlertItem | null>(null)
  const [viewing, setViewing] = useState<RobotMediaItem | null>(null)
  const [confirmClear, setConfirmClear] = useState(false)
  const [clearing, setClearing] = useState(false)
  const [clearError, setClearError] = useState<string | null>(null)
  // Alerts whose older occurrences are listed as rows; every alert starts as one row.
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(() => new Set())
  const toggleRepeats = useCallback((alertId: string) => {
    setExpanded(prev => {
      const next = new Set(prev)

      if (!next.delete(alertId)) next.add(alertId)

      return next
    })
  }, [])

  const alerts = useMemo(
    () => filterAlerts(mergeAlertLog(feed.fetched, storeAlerts, feed.seen), scope, severityFilter),
    [feed.fetched, feed.seen, storeAlerts, scope, severityFilter],
  )
  const model = useMemo(
    () => buildLogEntries(alerts, scope.kind === 'robot' ? feed.media : [], { expanded }),
    [alerts, feed.media, scope.kind, expanded],
  )
  // Open = the LIVE store, filtered the same way — the number the header bell shows in fleet scope.
  const openIds = useMemo(
    () => filterAlerts(storeAlerts, scope, severityFilter).map(a => a.alertId),
    [storeAlerts, scope, severityFilter],
  )

  // Resolve the ENTIRE current scope server-side (robot + severity), not just the rows shown, so
  // the backlog beyond the cap is cleared too. The rows stay: they come from the fetch, which is
  // re-run on success so each flips to "resolved" instead of vanishing.
  const handleClearAll = async () => {
    setClearing(true)
    setClearError(null)
    try {
      await clearAll(
        {
          robotId: scope.kind === 'robot' ? scope.robotId : undefined,
          // NORMALISED: this becomes a Mongo `$in`, and the legacy `warning` matches nothing.
          severity: severityFilter?.map(normalizeSeverity),
        },
        openIds,
      )
      setConfirmClear(false)
      void feed.refetch()
    } catch (err) {
      setClearError(errorText(err, t('alerts.errors.clearAllFailed', "Couldn't clear the alerts. Please try again.")))
    } finally {
      setClearing(false)
    }
  }

  const modals = (
    <>
      <EnrollFaceModal
        open={!!enrollAlert}
        snapshotRef={enrollAlert?.snapshotRef ?? ''}
        defaultName={enrollAlert?.identity ?? enrollAlert?.name ?? ''}
        robotId={enrollAlert?.robotId}
        onClose={() => setEnrollAlert(null)}
      />
      {viewing && (
        <RobotMediaLightbox items={[viewing]} index={0} onClose={() => setViewing(null)} onIndexChange={() => {}} />
      )}
    </>
  )

  if (model.entries.length === 0) {
    return (
      <div className="flex h-full min-h-0 flex-col p-4">
        <EmptyState
          icon={<BellOff className="h-8 w-8" />}
          title={t('alerts.empty.title', 'No alerts yet')}
          body={t('alerts.empty.body', 'Alarms and pictures will appear here as they arrive.')}
        />
        {modals}
      </div>
    )
  }

  // Which population the counts describe: the wearer (then device) from the roster, then any
  // alert's captured name, then — only as a last resort — the id.
  const device = scope.kind === 'robot' ? rosterById.get(scope.robotId) : undefined
  const scopeName =
    scope.kind === 'robot'
      ? (device?.wearerName ?? device?.name ?? alerts.find(a => a.robotId === scope.robotId)?.name ?? scope.robotId)
      : null
  const filtered = (severityFilter?.length ?? 0) > 0
  const scopeLabel = [scopeName, filtered ? t('alerts.filtered', 'filtered') : null].filter(Boolean).join(' · ')

  const deviceLabel = (robotId: string) => {
    const d = rosterById.get(robotId)

    return d?.wearerName ?? d?.name ?? null
  }

  const truncated = model.total > model.entries.length
  const capped = feed.capped.alerts || feed.capped.pictures

  return (
    <div className="flex h-full min-h-0 flex-col">
      <AlertLogHeader
        openCount={openIds.length}
        entryCount={model.total}
        pictureCount={model.pictureCount}
        showPictures={scope.kind === 'robot'}
        scopeLabel={scopeLabel}
        onClearPins={openIds.length > 0 ? () => setConfirmClear(true) : undefined}
      />
      {feed.error && (
        <p
          role="alert"
          className="flex shrink-0 items-center gap-2 border-b border-red-200 bg-red-50 px-4 py-1.5 text-xs text-red-700 dark:border-red-900/40 dark:bg-red-900/20 dark:text-red-300"
        >
          {t('alerts.log.loadFailed', "Couldn't load the alert history.")}
          <button type="button" onClick={() => void feed.refetch()} className="font-medium underline">
            {t('alerts.log.retry', 'Retry')}
          </button>
        </p>
      )}
      <ul
        data-testid="alert-log"
        className="min-h-0 flex-1 divide-y divide-gray-100 overflow-y-auto dark:divide-gray-800"
      >
        {model.entries.map(entry =>
          entry.kind === 'alarm' ? (
            <AlertLogRow
              key={entry.key}
              entry={entry}
              label={resolveAlertLabel(entry.alert, rosterById)}
              kind={resolveDeviceKind(entry.alert, rosterById)}
              bridge={bridge}
              onEnroll={setEnrollAlert}
              onOpenPicture={setViewing}
              onToggleRepeats={toggleRepeats}
            />
          ) : entry.kind === 'picture' ? (
            <PictureLogRow
              key={entry.key}
              entry={entry}
              label={deviceLabel(entry.media.robotId)}
              bridge={bridge}
              onOpen={setViewing}
            />
          ) : (
            <li key={entry.key} className="px-3 py-1 text-[11px] text-gray-400 dark:text-gray-500">
              {t('alerts.log.earlier', '{{count}} earlier occurrences not listed', { count: entry.count })}
            </li>
          ),
        )}
      </ul>
      {(truncated || capped) && (
        <p className="shrink-0 border-t border-gray-200/60 px-3 py-1 text-[11px] text-gray-400 dark:border-gray-700/60 dark:text-gray-500">
          {truncated &&
            t('alerts.log.truncated', 'Showing the newest {{shown}} of {{total}} entries', {
              shown: model.entries.length,
              total: model.total,
            })}
          {truncated && capped && ' · '}
          {capped &&
            t(
              'alerts.log.serverCap',
              'The server keeps the newest {{count}} alarms and pictures; older ones are not listed.',
              {
                count: SERVER_LIST_CAP,
              },
            )}
        </p>
      )}
      {confirmClear && (
        <ClearAllConfirm
          title={t('alerts.clearAll.confirmTitle', 'Clear all map pins?')}
          body={t(
            'alerts.clearAll.confirmBody',
            'Resolve every open alert in this scope so its map pin is removed — including any not listed here. Nothing is removed from this log.',
          )}
          confirmLabel={t('alerts.clearAll.confirmCta', 'Clear pins')}
          cancelLabel={t('alerts.clearAll.cancel', 'Cancel')}
          busy={clearing}
          error={clearError ?? undefined}
          onConfirm={() => void handleClearAll()}
          onClose={() => {
            setConfirmClear(false)
            setClearError(null)
          }}
        />
      )}
      {modals}
    </div>
  )
}
